import { supabase } from '../lib/supabase';

export const socialAccountService = {
  // Get all social accounts for a brand
  async getByBrandId(brandId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Verify user owns the brand
    const { data: brand } = await supabase?.from('brands')?.select('id')?.eq('id', brandId)?.eq('user_id', user?.id)?.single();

    if (!brand) throw new Error('Brand not found or access denied');

    const { data, error } = await supabase?.from('social_accounts')?.select('*')?.eq('brand_id', brandId)?.order('platform', { ascending: true });

    if (error) throw error;

    return data?.map(account => ({
      id: account?.id,
      brandId: account?.brand_id,
      platform: account?.platform,
      accountName: account?.account_name,
      accountUrl: account?.account_url,
      isConnected: account?.is_connected,
      followerCount: account?.follower_count,
      lastSyncedAt: account?.last_synced_at,
      createdAt: account?.created_at,
      updatedAt: account?.updated_at
    }));
  },

  // Connect social account to brand
  async connect(brandId, accountData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Verify user owns the brand
    const { data: brand } = await supabase?.from('brands')?.select('id')?.eq('id', brandId)?.eq('user_id', user?.id)?.single();

    if (!brand) throw new Error('Brand not found or access denied');

    const { data, error } = await supabase?.from('social_accounts')?.insert({
        brand_id: brandId,
        platform: accountData?.platform,
        account_name: accountData?.accountName,
        account_url: accountData?.accountUrl || null,
        access_token: accountData?.accessToken || null,
        refresh_token: accountData?.refreshToken || null,
        token_expires_at: accountData?.tokenExpiresAt || null,
        is_connected: true,
        follower_count: accountData?.followerCount || 0,
        last_synced_at: new Date()?.toISOString()
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      brandId: data?.brand_id,
      platform: data?.platform,
      accountName: data?.account_name,
      accountUrl: data?.account_url,
      isConnected: data?.is_connected,
      followerCount: data?.follower_count,
      lastSyncedAt: data?.last_synced_at,
      createdAt: data?.created_at
    };
  },

  // Disconnect social account
  async disconnect(accountId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Verify user owns the brand that owns this account
    const { data: account } = await supabase?.from('social_accounts')?.select('brand_id, brands!inner(user_id)')?.eq('id', accountId)?.single();

    if (!account || account?.brands?.user_id !== user?.id) {
      throw new Error('Social account not found or access denied');
    }

    const { error } = await supabase?.from('social_accounts')?.delete()?.eq('id', accountId);

    if (error) throw error;
    return { success: true };
  },

  // Update social account details
  async update(accountId, accountData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Verify user owns the brand that owns this account
    const { data: account } = await supabase?.from('social_accounts')?.select('brand_id, brands!inner(user_id)')?.eq('id', accountId)?.single();

    if (!account || account?.brands?.user_id !== user?.id) {
      throw new Error('Social account not found or access denied');
    }

    const updateData = {};
    if (accountData?.accountName !== undefined) updateData.account_name = accountData?.accountName;
    if (accountData?.accountUrl !== undefined) updateData.account_url = accountData?.accountUrl;
    if (accountData?.followerCount !== undefined) updateData.follower_count = accountData?.followerCount;
    if (accountData?.isConnected !== undefined) updateData.is_connected = accountData?.isConnected;

    const { data, error } = await supabase?.from('social_accounts')?.update(updateData)?.eq('id', accountId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      brandId: data?.brand_id,
      platform: data?.platform,
      accountName: data?.account_name,
      accountUrl: data?.account_url,
      isConnected: data?.is_connected,
      followerCount: data?.follower_count,
      lastSyncedAt: data?.last_synced_at
    };
  },

  // Sync follower counts
  async syncFollowers(accountId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // In production, this would call the actual social media API
    // For now, we just update the last_synced_at timestamp
    const { data, error } = await supabase?.from('social_accounts')?.update({
        last_synced_at: new Date()?.toISOString()
      })?.eq('id', accountId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      lastSyncedAt: data?.last_synced_at
    };
  }
};